import { useCallback, useState } from 'react';

/**
 * Page size, shared by every screen that lists garments.
 *
 * ⚠️ **The API caps `limit` at 100 and defaults to 20** (`catalog/schemas.ts`),
 * and a value above the cap is *clamped, not rejected* — asking for 1000 returns
 * 100 rows and looks exactly like a store that only has 100 garments. So the
 * options here stay well inside it and are the only values any screen sends.
 *
 * Paging exists to bound how many images a device loads at once, which is why 20
 * is the default rather than the largest option: a shop tablet on shop wifi
 * fetches twenty thumbnails comfortably and fifty noticeably less so. The larger
 * sizes are there for whoever is working through the admin table on a laptop.
 */
export const PAGE_SIZE_OPTIONS = [20, 30, 50] as const;

export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export const DEFAULT_PAGE_SIZE: PageSize = 20;

function isPageSize(value: number): value is PageSize {
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(value);
}

/**
 * Scopes are stored separately on purpose: the customer grid and the admin table
 * are used by different people on different hardware, and a choice made in one
 * has no business changing the other.
 */
export type PageSizeScope = 'catalog' | 'admin-garments';

const storageKey = (scope: PageSizeScope): string => `shringaar.pageSize.${scope}`;

function read(scope: PageSizeScope): PageSize {
  try {
    const stored = Number(localStorage.getItem(storageKey(scope)));
    return isPageSize(stored) ? stored : DEFAULT_PAGE_SIZE;
  } catch {
    // Private mode, or storage disabled. The default is always usable.
    return DEFAULT_PAGE_SIZE;
  }
}

/**
 * `localStorage` rather than `sessionStorage`: unlike the photo key, this is a
 * property of the device and whoever uses it, not of one customer's visit.
 */
export function usePageSize(scope: PageSizeScope): [PageSize, (next: PageSize) => void] {
  const [pageSize, setState] = useState<PageSize>(() => read(scope));

  const set = useCallback(
    (next: PageSize) => {
      setState(next);
      try {
        localStorage.setItem(storageKey(scope), String(next));
      } catch {
        /* the in-memory value still applies for this session */
      }
    },
    [scope],
  );

  return [pageSize, set];
}
